const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

class ApiService {
  constructor() {
    this.baseUrl = API_URL;
    this.accessToken = localStorage.getItem('lotsense_access_token');
    this.refreshToken = localStorage.getItem('lotsense_refresh_token');
    this.isRefreshing = false;
    this.refreshSubscribers = [];
  }

  setTokens(accessToken, refreshToken) {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    if (accessToken) {
      localStorage.setItem('lotsense_access_token', accessToken);
    } else {
      localStorage.removeItem('lotsense_access_token');
    }
    if (refreshToken) {
      localStorage.setItem('lotsense_refresh_token', refreshToken);
    } else {
      localStorage.removeItem('lotsense_refresh_token');
    }
  }

  clearTokens() {
    this.accessToken = null;
    this.refreshToken = null;
    localStorage.removeItem('lotsense_access_token');
    localStorage.removeItem('lotsense_refresh_token');
  }

  getAccessToken() {
    return this.accessToken || localStorage.getItem('lotsense_access_token');
  }

  getRefreshToken() {
    return this.refreshToken || localStorage.getItem('lotsense_refresh_token');
  }

  // Subscribe to token refresh
  subscribeTokenRefresh(callback) {
    this.refreshSubscribers.push(callback);
  }

  // Notify all subscribers with new token
  onTokenRefreshed(token) {
    this.refreshSubscribers.forEach(callback => callback(token));
    this.refreshSubscribers = [];
  }

  // Refresh the access token
  async refreshAccessToken() {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token');
    }

    const response = await fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      throw new Error('Token refresh failed');
    }

    const data = await response.json();
    this.setTokens(data.accessToken, this.getRefreshToken());
    return data.accessToken;
  }

  async request(endpoint, options = {}, retry = true) {
    const url = `${API_URL}${endpoint}`;
    const token = this.getAccessToken();

    // 60s timeout to prevent indefinite hang (e.g. slow/hanging server)
    const controller = new AbortController();
    const timeoutMs = 60000;
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    const config = {
      method: 'GET',
      ...options,
      signal: controller.signal,
      headers: {
        // Only set Content-Type if not a FormData upload
        ...(!(options.body instanceof FormData) && { 'Content-Type': 'application/json' }),
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
    };

    console.log('[API.request] Making request:', {
      url,
      method: config.method,
      hasBody: !!config.body,
      hasToken: !!token,
      headers: config.headers
    });

    try {
      console.log('[API.request] Calling fetch...');
      const response = await fetch(url, config);
      clearTimeout(timeoutId);
      console.log('[API.request] Fetch completed, status:', response.status);
      
      // Handle 401 - try to refresh token
      if (response.status === 401 && retry && this.getRefreshToken()) {
        clearTimeout(timeoutId);
        if (!this.isRefreshing) {
          this.isRefreshing = true;
          try {
            const newToken = await this.refreshAccessToken();
            this.isRefreshing = false;
            this.onTokenRefreshed(newToken);
            // Retry the original request
            return this.request(endpoint, options, false);
          } catch (refreshError) {
            this.isRefreshing = false;
            this.clearTokens();
            window.location.href = '/login';
            throw new Error('Session expired');
          }
        } else {
          // Wait for token refresh
          return new Promise((resolve, reject) => {
            this.subscribeTokenRefresh(async (newToken) => {
              try {
                const result = await this.request(endpoint, options, false);
                resolve(result);
              } catch (err) {
                reject(err);
              }
            });
          });
        }
      }

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          this.clearTokens();
          window.location.href = '/login';
        }
        const error = new Error(data.message || data.error || 'Request failed');
        error.details = data.details;
        error.status = response.status;
        // Include duplicates data for 409 conflict responses (duplicate detection)
        if (response.status === 409 && data.duplicates) {
          error.duplicates = data.duplicates;
          error.message = data.message || data.error;
        }
        // Include skipped deals for batch-mark-paid 400 responses
        if (data.skippedDeals) {
          error.skippedDeals = data.skippedDeals;
        }
        throw error;
      }

      return data;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        const timeoutErr = new Error('Request timed out. The server may be slow or busy - please try again.');
        timeoutErr.name = 'TimeoutError';
        throw timeoutErr;
      }
      console.error('API Error:', error);
      throw error;
    }
  }

  // Auth
  async login(email, password) {
    const data = await this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }, false);
    this.setTokens(data.accessToken, data.refreshToken);
    return data;
  }

  // Dev quick login (only works when the server runs with ALLOW_DEV_LOGIN=true)
  async getDevLoginInfo() {
    try {
      return await this.request('/auth/dev-login/enabled', {}, false);
    } catch {
      return { enabled: false, personas: [] };
    }
  }

  async devLogin(persona) {
    const data = await this.request('/auth/dev-login', {
      method: 'POST',
      body: JSON.stringify({ persona }),
    }, false);
    this.setTokens(data.accessToken, data.refreshToken);
    return data;
  }

  async logout() {
    try {
      await this.request('/auth/logout', { method: 'POST' }, false);
    } finally {
      this.clearTokens();
    }
  }

  async getCurrentUser() {
    return this.request('/auth/me');
  }

  async changePassword(currentPassword, newPassword) {
    return this.request('/auth/password', {
      method: 'PUT',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  // ============ FILE UPLOADS ============
  
  // Upload a single file (video, document, or image)
  async uploadFile(file, type = 'inspections') {
    const formData = new FormData();
    formData.append('file', file);
    
    const token = this.getAccessToken();
    const response = await fetch(`${API_URL}/uploads/file/${type}`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });
    
    // Handle 401 - try to refresh token
    if (response.status === 401 && this.getRefreshToken()) {
      if (!this.isRefreshing) {
        this.isRefreshing = true;
        try {
          const newToken = await this.refreshAccessToken();
          this.isRefreshing = false;
          this.onTokenRefreshed(newToken);
          // Retry the upload with new token
          const retryResponse = await fetch(`${API_URL}/uploads/file/${type}`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${newToken}`,
            },
            body: formData,
          });
          
          if (!retryResponse.ok) {
            const error = await retryResponse.json().catch(() => ({ error: 'Upload failed' }));
            throw new Error(error.error || 'Upload failed');
          }
          
          return retryResponse.json();
        } catch (refreshError) {
          this.isRefreshing = false;
          this.clearTokens();
          window.location.href = '/login';
          throw new Error('Session expired');
        }
      }
    }
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.error || 'Upload failed');
    }
    
    return response.json();
  }

  // Upload video file
  async uploadVideo(file) {
    return this.uploadFile(file, 'videos');
  }

  // Upload document (PDF, etc.)
  async uploadDocument(file) {
    return this.uploadFile(file, 'documents');
  }

  // Upload image
  async uploadImage(file) {
    return this.uploadFile(file, 'inspections');
  }

  // Upload multiple photos
  async uploadPhotos(files, type = 'inspections') {
    const formData = new FormData();
    files.forEach(file => formData.append('photos', file));
    
    const token = this.getAccessToken();
    const response = await fetch(`${API_URL}/uploads/${type}`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.error || 'Upload failed');
    }
    
    return response.json();
  }

  // Users
  async getUsers() {
    return this.request('/users');
  }

  async getUser(id) {
    return this.request(`/users/${id}`);
  }

  async createUser(userData) {
    return this.request('/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async updateUser(id, userData) {
    return this.request(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  async deleteUser(id) {
    return this.request(`/users/${id}`, { method: 'DELETE' });
  }

  // Vehicles
  async getVehicles(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/vehicles${query ? `?${query}` : ''}`);
  }

  async getVehicleStats() {
    return this.request('/vehicles/stats');
  }

  async getVehicle(id) {
    return this.request(`/vehicles/${id}`);
  }

  async createVehicle(vehicleData) {
    return this.request('/vehicles', {
      method: 'POST',
      body: JSON.stringify(vehicleData),
    });
  }

  async updateVehicle(id, vehicleData) {
    return this.request(`/vehicles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(vehicleData),
    });
  }

  async updateVehicleWorkflow(id, workflowData) {
    return this.request(`/vehicles/${id}/workflow`, {
      method: 'PUT',
      body: JSON.stringify(workflowData),
    });
  }

  async deleteVehicle(id, hardDelete = false) {
    const url = hardDelete ? `/vehicles/${id}?hard=true` : `/vehicles/${id}`;
    return this.request(url, { method: 'DELETE' });
  }

  async syncSoldStatus() {
    return this.request('/vehicles/sync-sold-status', { method: 'POST' });
  }

  async markSoldByVin(vin) {
    return this.request('/vehicles/mark-sold-by-vin', {
      method: 'POST',
      body: JSON.stringify({ vin }),
    });
  }

  async getDeletedVehicles() {
    return this.request('/vehicles/deleted/list');
  }

  async restoreVehicle(id) {
    return this.request(`/vehicles/restore/${id}`, { method: 'POST' });
  }

  async restoreVehicleByVin(vin) {
    return this.request('/vehicles/restore-by-vin', {
      method: 'POST',
      body: JSON.stringify({ vin }),
    });
  }

  // ARB Deadline Alerts
  async getArbAlerts() {
    return this.request('/vehicles/arb-alerts');
  }

  async getArbDashboard(tab = 'all') {
    return this.request(`/vehicles/arb-dashboard?tab=${tab}`);
  }

  async updateArbAction(vehicleId, action, notes, arbData = {}) {
    return this.request(`/vehicles/${vehicleId}/arb-action`, {
      method: 'PUT',
      body: JSON.stringify({ action, notes, ...arbData }),
    });
  }

  async checkArbReminders() {
    return this.request('/vehicles/arb-reminders');
  }

  // Shop/Personal Vehicles
  async getShopVehicles() {
    return this.request('/vehicles/shop-vehicles');
  }

  async createShopVehicle(vehicleData) {
    return this.request('/vehicles/shop-vehicle', {
      method: 'POST',
      body: JSON.stringify(vehicleData),
    });
  }

  // Customer Vehicles
  async getCustomerVehicles() {
    return this.request('/vehicles/customer-vehicles');
  }

  async createCustomerVehicle(vehicleData) {
    return this.request('/vehicles/customer-vehicle', {
      method: 'POST',
      body: JSON.stringify(vehicleData),
    });
  }

  // Analytics
  async getAnalyticsBottlenecks() {
    return this.request('/analytics/bottlenecks');
  }

  async getAnalyticsMetrics(startDate, endDate) {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return this.request(`/analytics/metrics?${params.toString()}`);
  }

  async getVehicleTimeline(vehicleId) {
    return this.request(`/analytics/vehicle/${vehicleId}/timeline`);
  }

  async getAuctionPerformance(startDate, endDate) {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return this.request(`/analytics/auctions?${params.toString()}`);
  }

  async getRepairTypeAnalysis(startDate, endDate) {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return this.request(`/analytics/repair-types?${params.toString()}`);
  }

  async getMakeModelAnalysis(startDate, endDate) {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return this.request(`/analytics/make-model?${params.toString()}`);
  }

  async getVendorPerformance(startDate, endDate) {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return this.request(`/analytics/vendors?${params.toString()}`);
  }

  async getWeeklyReport(weekOf) {
    const params = weekOf ? `?weekOf=${weekOf}` : '';
    return this.request(`/analytics/weekly-report${params}`);
  }

  async getAnalyticsVehicles(startDate, endDate, status) {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (status) params.append('status', status);
    return this.request(`/analytics/vehicles?${params.toString()}`);
  }

  // Expenses
  async getExpenses(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/expenses${query ? `?${query}` : ''}`);
  }

  async createExpense(expenseData) {
    return this.request('/expenses', {
      method: 'POST',
      body: JSON.stringify(expenseData),
    });
  }

  async updateExpense(id, expenseData) {
    return this.request(`/expenses/${id}`, {
      method: 'PUT',
      body: JSON.stringify(expenseData),
    });
  }

  async deleteExpense(id) {
    return this.request(`/expenses/${id}`, { method: 'DELETE' });
  }

  // Vendors
  async getVendors(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/vendors${query ? `?${query}` : ''}`);
  }

  async getAuctions() {
    return this.request('/vendors/auctions');
  }

  async getTransportVendors() {
    return this.request('/vendors/transport');
  }

  async getVendor(id) {
    return this.request(`/vendors/${id}`);
  }

  async createVendor(vendorData) {
    // Use quick-add endpoint for simple vendor creation
    return this.request('/vendors/quick-add', {
      method: 'POST',
      body: JSON.stringify(vendorData),
    });
  }

  async createVendorFull(vendorData) {
    return this.request('/vendors', {
      method: 'POST',
      body: JSON.stringify(vendorData),
    });
  }

  async updateVendor(id, vendorData) {
    return this.request(`/vendors/${id}`, {
      method: 'PUT',
      body: JSON.stringify(vendorData),
    });
  }

  async deleteVendor(id) {
    return this.request(`/vendors/${id}`, { method: 'DELETE' });
  }

  // Parts
  async getParts(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/parts${query ? `?${query}` : ''}`);
  }

  async createPart(partData) {
    return this.request('/parts', {
      method: 'POST',
      body: JSON.stringify(partData),
    });
  }

  async updatePart(id, partData) {
    return this.request(`/parts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(partData),
    });
  }

  async deletePart(id) {
    return this.request(`/parts/${id}`, { method: 'DELETE' });
  }

  // Pre-Arrival Parts
  async getPreArrivalParts() {
    return this.request('/parts/pre-arrival');
  }

  async createPreArrivalPart(data) {
    return this.request('/parts/pre-arrival', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getVehiclePreArrivalParts(vehicleId) {
    return this.request(`/parts/pre-arrival/vehicle/${vehicleId}`);
  }

  async transferPreArrivalPartsToRO(vehicleId, repairOrderId) {
    return this.request('/parts/transfer-to-ro', {
      method: 'POST',
      body: JSON.stringify({ vehicleId, repairOrderId }),
    });
  }

  // Tasks
  async getTasks(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/tasks${query ? `?${query}` : ''}`);
  }

  async getMyTasks() {
    return this.request('/tasks/my');
  }

  async createTask(taskData) {
    return this.request('/tasks', {
      method: 'POST',
      body: JSON.stringify(taskData),
    });
  }

  async updateTask(id, taskData) {
    return this.request(`/tasks/${id}`, {
      method: 'PUT',
      body: JSON.stringify(taskData),
    });
  }

  async deleteTask(id) {
    return this.request(`/tasks/${id}`, { method: 'DELETE' });
  }

  // File Uploads
  async uploadPhotos(type, files, onProgress) {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('photos', file);
    });

    const token = this.getAccessToken();

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && onProgress) {
          const percent = Math.round((e.loaded / e.total) * 100);
          onProgress(percent);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText));
          } catch (e) {
            reject(new Error('Invalid response from server'));
          }
        } else {
          try {
            const errorData = JSON.parse(xhr.responseText);
            reject(new Error(errorData.error || 'Upload failed'));
          } catch (e) {
            reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
          }
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed - network error'));
      });

      xhr.open('POST', `${API_URL}/uploads/${type}`);
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
      xhr.send(formData);
    });
  }

  async deletePhoto(type, filename) {
    return this.request(`/uploads/${type}/${filename}`, { method: 'DELETE' });
  }

  // Buyers
  async getBuyers() {
    return this.request('/buyers');
  }

  async createBuyer(buyerData) {
    return this.request('/buyers', {
      method: 'POST',
      body: JSON.stringify(buyerData),
    });
  }

  // Payment Methods
  async getPaymentMethods() {
    return this.request('/payment-methods');
  }

  async createPaymentMethod(data) {
    return this.request('/payment-methods', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Transport
  async getTransportList(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/transport${query ? `?${query}` : ''}`);
  }

  async getTransportStats() {
    return this.request('/transport/stats');
  }

  async updateTransportStatus(vehicleId, data) {
    return this.request(`/transport/${vehicleId}/status`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async updateTransportNotes(vehicleId, notes) {
    return this.request(`/transport/${vehicleId}/notes`, {
      method: 'PUT',
      body: JSON.stringify({ notes }),
    });
  }

  // Notifications
  async getNotifications(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/notifications${query ? `?${query}` : ''}`);
  }

  async getNotificationCount() {
    return this.request('/notifications/count');
  }

  async markNotificationRead(id) {
    return this.request(`/notifications/${id}/read`, { method: 'PUT' });
  }

  async markAllNotificationsRead() {
    return this.request('/notifications/read-all', { method: 'PUT' });
  }

  // Audit Logs
  async getAuditLogs(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/audit${query ? `?${query}` : ''}`);
  }

  async getEntityAuditLogs(entityType, entityId, limit = 50) {
    return this.request(`/audit/entity/${entityType}/${entityId}?limit=${limit}`);
  }

  // Task Logs (for admin)
  async getTaskLogs() {
    return this.request('/tasks/logs');
  }

  // Accounting
  async getBankAccounts() {
    return this.request('/accounting/bank-accounts');
  }

  async getDashboardSummary() {
    return this.request('/accounting/dashboard-summary');
  }

  async updateBankAccount(id, data) {
    return this.request(`/accounting/bank-accounts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getAccountingEntries(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/accounting/entries${query ? `?${query}` : ''}`);
  }

  async getAllTransactions(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/accounting/all-transactions${query ? `?${query}` : ''}`);
  }

  async recoverTransportCosts(dryRun = false) {
    return this.request('/vehicles/recover-transport-costs', {
      method: 'POST',
      body: JSON.stringify({ dryRun }),
    });
  }

  async createAccountingEntry(data) {
    return this.request('/accounting/entries', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async markPaymentCleared(data) {
    return this.request('/accounting/payment-cleared', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async fixFloorplanPending() {
    return this.request('/accounting/fix-floorplan-pending', { method: 'POST' });
  }

  async bulkMarkCleared() {
    return this.request('/accounting/bulk-mark-cleared', { method: 'POST' });
  }

  async backfillDealPayments() {
    return this.request('/accounting/backfill-deal-payments', { method: 'POST' });
  }

  async recalculateBalances() {
    return this.request('/accounting/recalculate-balances', { method: 'POST' });
  }

  async createTransaction(data) {
    return this.request('/accounting/transactions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getVehicleAccounting(vehicleId) {
    return this.request(`/accounting/vehicle/${vehicleId}`);
  }

  async addFundsToBankAccount(accountId, data) {
    return this.request(`/accounting/bank-accounts/${accountId}/add-funds`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async transferFunds(fromAccountId, toAccountId, data) {
    return this.request(`/accounting/bank-accounts/${fromAccountId}/transfer/${toAccountId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Accounting Categories
  async getAccountingCategories(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/accounting/categories${query ? `?${query}` : ''}`);
  }

  async createAccountingCategory(data) {
    return this.request('/accounting/categories', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAccountingCategory(id, data) {
    return this.request(`/accounting/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteAccountingCategory(id) {
    return this.request(`/accounting/categories/${id}`, { method: 'DELETE' });
  }

  async backfillCategories() {
    return this.request('/accounting/categories/backfill', { method: 'POST' });
  }

  async mergeCategories(sourceId, targetId) {
    return this.request(`/accounting/categories/merge`, {
      method: 'POST',
      body: JSON.stringify({ sourceId, targetId }),
    });
  }

  // Vehicle Costs & Sales
  async getVehicleCosts(vehicleId) {
    return this.request(`/accounting/vehicle/${vehicleId}/costs`);
  }

  async getVehicleAllCosts(vehicleId) {
    return this.request(`/accounting/vehicle/${vehicleId}/all-costs`);
  }

  async sellVehicle(vehicleId, data) {
    return this.request(`/accounting/vehicle/${vehicleId}/sell`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Reports
  async getRolloverReport(asOfDate = null) {
    const query = asOfDate ? `?asOfDate=${asOfDate}` : '';
    return this.request(`/accounting/reports/rollover${query}`);
  }

  async getProfitLossReport(startDate = null, endDate = null) {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const query = params.toString();
    return this.request(`/accounting/reports/profit-loss${query ? `?${query}` : ''}`);
  }

  async getReport(reportName, params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/reports/${reportName}${query ? `?${query}` : ''}`);
  }

  // Repair Orders
  async getRepairOrders(filters = {}) {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.assignedTo) params.append('assignedTo', filters.assignedTo);
    if (filters.vehicleId) params.append('vehicleId', filters.vehicleId);
    const query = params.toString();
    return this.request(`/repair-orders${query ? `?${query}` : ''}`);
  }

  async getRepairOrderStats() {
    return this.request('/repair-orders/stats');
  }

  // Get ROs assigned to current user (for shop dashboard)
  async getMyRepairOrders() {
    return this.request('/repair-orders/my');
  }

  async getRepairOrder(id) {
    return this.request(`/repair-orders/${id}`);
  }

  async createRepairOrder(data) {
    return this.request('/repair-orders', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateRepairOrder(id, data) {
    return this.request(`/repair-orders/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteRepairOrder(id) {
    return this.request(`/repair-orders/${id}`, {
      method: 'DELETE',
    });
  }

  async addRepairOrderPart(roId, data) {
    return this.request(`/repair-orders/${roId}/parts`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateRepairOrderPart(roId, partId, data) {
    return this.request(`/repair-orders/${roId}/parts/${partId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async addRepairOrderLabor(roId, data) {
    return this.request(`/repair-orders/${roId}/labor`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getVehicleRepairOrders(vehicleId) {
    return this.request(`/repair-orders/vehicle/${vehicleId}`);
  }

  async deleteRepairOrderPart(roId, partId) {
    return this.request(`/repair-orders/${roId}/parts/${partId}`, {
      method: 'DELETE',
    });
  }

  // Snooze RO - hide from dashboard for X days
  async snoozeRepairOrder(roId, days, reason) {
    return this.request(`/repair-orders/${roId}/snooze`, {
      method: 'POST',
      body: JSON.stringify({ days, reason }),
    });
  }

  // Add admin explanation for 15+ day old RO
  async addAdminExplanation(roId, explanation) {
    return this.request(`/repair-orders/${roId}/admin-explanation`, {
      method: 'POST',
      body: JSON.stringify({ explanation }),
    });
  }

  // RO Outside Vendors
  async addRepairOrderVendor(roId, data) {
    return this.request(`/repair-orders/${roId}/vendors`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateRepairOrderVendor(roId, vendorRecordId, data) {
    return this.request(`/repair-orders/${roId}/vendors/${vendorRecordId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteRepairOrderVendor(roId, vendorRecordId) {
    return this.request(`/repair-orders/${roId}/vendors/${vendorRecordId}`, {
      method: 'DELETE',
    });
  }
  addROVendor(roId, data) { return this.addRepairOrderVendor(roId, data); }
  updateROVendor(roId, vendorRecordId, data) { return this.updateRepairOrderVendor(roId, vendorRecordId, data); }
  deleteROVendor(roId, vendorRecordId) { return this.deleteRepairOrderVendor(roId, vendorRecordId); }

  async markVendorComplete(roId, vendorType, actualCost) {
    return this.request(`/repair-orders/${roId}/vendors/mark-complete`, {
      method: 'POST',
      body: JSON.stringify({ vendorType, actualCost }),
    });
  }

  // RO Approval Workflow
  async submitROForApproval(roId) {
    return this.request(`/repair-orders/${roId}/submit-for-approval`, {
      method: 'POST',
    });
  }

  // RO Estimate Only toggle (quote mode)
  async setRepairOrderEstimateOnly(roId, enabled) {
    return this.request(`/repair-orders/${roId}/estimate-only`, {
      method: 'POST',
      body: JSON.stringify({ enabled }),
    });
  }

  async approveRO(roId, notes = null) {
    return this.request(`/repair-orders/${roId}/approve`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    });
  }

  // Cancel a part (soft delete - keeps data, removes from costs)
  async cancelROPart(roId, partId, reason = null) {
    return this.request(`/repair-orders/${roId}/parts/${partId}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  // Restore a cancelled part
  async restoreROPart(roId, partId) {
    return this.request(`/repair-orders/${roId}/parts/${partId}/restore`, {
      method: 'POST',
    });
  }

  async denyRO(roId, notes) {
    return this.request(`/repair-orders/${roId}/deny`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    });
  }

  async getPendingApprovalROs() {
    return this.request('/repair-orders/pending-approval');
  }

  // RO Work Items
  async addROWorkItem(roId, data) {
    return this.request(`/repair-orders/${roId}/work-items`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateROWorkItem(roId, itemId, data) {
    return this.request(`/repair-orders/${roId}/work-items/${itemId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteROWorkItem(roId, itemId) {
    return this.request(`/repair-orders/${roId}/work-items/${itemId}`, {
      method: 'DELETE',
    });
  }

  // RO History
  async getROHistory(roId) {
    return this.request(`/repair-orders/${roId}/history`);
  }

  // RO Uploads
  async updateROUploads(roId, data) {
    return this.request(`/repair-orders/${roId}/uploads`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // RO Dashboard
  async getRODashboardStats() {
    return this.request('/repair-orders/dashboard/stats');
  }

  async getRODashboardList(filters = {}) {
    const params = new URLSearchParams(filters).toString();
    return this.request(`/repair-orders/dashboard/list${params ? '?' + params : ''}`);
  }

  async getVendorNeeds() {
    return this.request('/repair-orders/dashboard/vendor-needs');
  }

  async syncVendorStatuses() {
    return this.request('/vehicles/sync-vendor-statuses', { method: 'POST' });
  }

  // ============ VEHICLE INSPECTIONS ============
  
  async getVehiclesNeedingInspection() {
    return this.request('/repair-orders/inspections/needs-inspection');
  }

  async getVehicleInspection(vehicleId) {
    return this.request(`/repair-orders/inspections/vehicle/${vehicleId}`);
  }

  async saveVehicleInspection(vehicleId, data) {
    return this.request(`/repair-orders/inspections/vehicle/${vehicleId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async completeInspection(vehicleId) {
    return this.request(`/repair-orders/inspections/vehicle/${vehicleId}/complete`, {
      method: 'POST',
    });
  }

  async markNoWorkNeeded(vehicleId) {
    return this.request(`/repair-orders/inspections/vehicle/${vehicleId}/no-work-needed`, {
      method: 'POST',
    });
  }

  async createROFromInspection(vehicleId, data) {
    return this.request(`/repair-orders/inspections/vehicle/${vehicleId}/create-ro`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getReconDashboardVehicles() {
    return this.request('/repair-orders/dashboard/vehicles');
  }

  async getPartsWaitingDelivery() {
    return this.request('/repair-orders/dashboard/parts-waiting-delivery');
  }

  async savePrecheckResults(data) {
    return this.request('/repair-orders/precheck', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getTasks(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/tasks${queryString ? '?' + queryString : ''}`);
  }

  async createNotification(data) {
    return this.request('/notifications', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getVendorPunchList(vendorType) {
    return this.request(`/repair-orders/dashboard/vendor-punch-list/${vendorType}`);
  }

  // ============ TIME CLOCK ============
  
  async getClockStatus() {
    return this.request('/time-clock/status');
  }

  async clockIn() {
    return this.request('/time-clock/clock-in', { method: 'POST' });
  }

  async clockOut(dailyNotes, tookLunch = false, vehicleIds = [], roIds = []) {
    return this.request('/time-clock/clock-out', {
      method: 'POST',
      body: JSON.stringify({ dailyNotes, tookLunch, vehicleIds, roIds }),
    });
  }

  async getTimeEntries(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/time-clock/entries${query ? '?' + query : ''}`);
  }

  async getTimeSummary(startDate, endDate) {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return this.request(`/time-clock/summary?${params.toString()}`);
  }

  async deleteTimeEntry(id) {
    return this.request(`/time-clock/${id}`, { method: 'DELETE' });
  }

  async updateTimeEntry(id, data) {
    return this.request(`/time-clock/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async createManualTimeEntry(data) {
    return this.request('/time-clock/manual-entry', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getWeekSummary(weekStart, userId) {
    const params = new URLSearchParams();
    if (weekStart) params.append('weekStart', weekStart);
    if (userId) params.append('userId', userId);
    const query = params.toString();
    return this.request(`/time-clock/week-summary${query ? '?' + query : ''}`);
  }

  async getWeeklyChecklist(weekStart) {
    const params = weekStart ? `?weekStart=${weekStart}` : '';
    return this.request(`/time-clock/checklist${params}`);
  }

  async saveWeeklyChecklist(data) {
    return this.request('/time-clock/checklist', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async skipWeeklyChecklist(weekStart) {
    return this.request('/time-clock/checklist/skip', {
      method: 'POST',
      body: JSON.stringify({ weekStart }),
    });
  }

  // ============ MESSAGING ============
  
  async getConversations() {
    return this.request('/messaging/conversations');
  }

  async getOrCreateDirectConversation(targetUserId) {
    return this.request('/messaging/conversations/direct', {
      method: 'POST',
      body: JSON.stringify({ targetUserId }),
    });
  }

  async createGroupConversation(name, participantIds) {
    return this.request('/messaging/conversations/group', {
      method: 'POST',
      body: JSON.stringify({ name, participantIds }),
    });
  }

  async getMessages(conversationId, options = {}) {
    const params = new URLSearchParams();
    if (options.limit) params.append('limit', options.limit);
    if (options.before) params.append('before', options.before);
    const query = params.toString();
    return this.request(`/messaging/conversations/${conversationId}/messages${query ? '?' + query : ''}`);
  }

  async sendMessage(conversationId, content, messageType = 'text') {
    return this.request(`/messaging/conversations/${conversationId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ content, messageType }),
    });
  }

  async markConversationRead(conversationId) {
    return this.request(`/messaging/conversations/${conversationId}/read`, {
      method: 'POST',
    });
  }

  async getUnreadMessageCount() {
    return this.request('/messaging/unread-count');
  }

  async getMessagingUsers() {
    return this.request('/messaging/users');
  }

  // ============ NOTIFICATIONS ============
  
  async getNotifications(options = {}) {
    const params = new URLSearchParams();
    if (options.unreadOnly) params.append('unreadOnly', 'true');
    if (options.limit) params.append('limit', options.limit);
    const query = params.toString();
    return this.request(`/notifications${query ? '?' + query : ''}`);
  }

  async getNotificationCount() {
    return this.request('/notifications/count');
  }

  async markNotificationRead(notificationId) {
    return this.request(`/notifications/${notificationId}/read`, {
      method: 'PUT',
    });
  }

  async markAllNotificationsRead() {
    return this.request('/notifications/read-all', {
      method: 'PUT',
    });
  }

  // ============ PARTS ROOM ============

  async getPartsRoom() {
    return this.request('/parts/room');
  }

  async addPartToRoom(partData) {
    return this.request('/parts/room', {
      method: 'POST',
      body: JSON.stringify(partData),
    });
  }

  async updatePartStatus(partId, status) {
    return this.request(`/parts/room/${partId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  }

  async requestPartReturn(partId, reason) {
    return this.request(`/parts/room/${partId}/return`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  async markReturnProcessed(partId) {
    return this.request(`/parts/room/${partId}/return-processed`, {
      method: 'POST',
    });
  }

  // ============ SHOP SUPPLIES ============

  async getShopSupplies() {
    return this.request('/parts/supplies');
  }

  async addShopSupply(supplyData) {
    return this.request('/parts/supplies', {
      method: 'POST',
      body: JSON.stringify(supplyData),
    });
  }

  async updateShopSupply(supplyId, supplyData) {
    return this.request(`/parts/supplies/${supplyId}`, {
      method: 'PUT',
      body: JSON.stringify(supplyData),
    });
  }

  async updateSupplyQuantity(supplyId, quantity) {
    return this.request(`/parts/supplies/${supplyId}/quantity`, {
      method: 'PUT',
      body: JSON.stringify({ quantity }),
    });
  }

  async getLowStockSupplies() {
    return this.request('/parts/supplies/low-stock');
  }

  // ============ PART RETURNS ============

  async getPartReturns() {
    return this.request('/parts/returns');
  }

  async processPartReturn(returnId, notes) {
    return this.request(`/parts/returns/${returnId}/process`, {
      method: 'PUT',
      body: JSON.stringify({ notes }),
    });
  }

  // ============ STOCK PARTS ============

  async getStockParts() {
    return this.request('/parts/stock');
  }

  async addStockPart(partData) {
    return this.request('/parts/stock', {
      method: 'POST',
      body: JSON.stringify(partData),
    });
  }

  async updateStockPartQuantity(partId, quantity) {
    return this.request(`/parts/stock/${partId}/quantity`, {
      method: 'PUT',
      body: JSON.stringify({ quantity }),
    });
  }

  async deleteStockPart(partId) {
    return this.request(`/parts/stock/${partId}`, {
      method: 'DELETE',
    });
  }

  // ============ ALEX BILLING ============
  async getAlexBilling() {
    return this.request('/vehicles/alex-billing');
  }

  async payAlexBilling(data) {
    return this.request('/vehicles/alex-billing/pay', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async zeroOutAllFees() {
    return this.request('/vehicles/alex-billing/zero-all', {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async zeroOutSelectedFees(data) {
    return this.request('/vehicles/alex-billing/zero-selected', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAlexBillingFee(data) {
    return this.request('/vehicles/alex-billing/update-fee', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ============ FLOORPLAN ============
  async getFloorplan() {
    return this.request('/vehicles/floorplan');
  }

  async getSoldVehicles() {
    return this.request('/vehicles/sold');
  }

  async updateFloorplanPayoff(vehicleId, payoffAmount) {
    return this.request(`/vehicles/${vehicleId}/floorplan`, {
      method: 'PUT',
      body: JSON.stringify({ payoffAmount }),
    });
  }

  async payoffFloorplan(vehicleId, data) {
    return this.request(`/vehicles/${vehicleId}/floorplan/payoff`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ============ BUYING POWER ============
  async getBuyingPower() {
    return this.request('/accounting/buying-power');
  }

  // ============ BUYER DASHBOARD ============
  async getBuyerDashboard() {
    return this.request('/accounting/buyer-dashboard');
  }

  // ============ BUYER BALANCES & DEPOSITS ============
  async getBuyerBalances() {
    return this.request('/accounting/buyer-balances');
  }

  async getUndepositedFunds() {
    return this.request('/accounting/undeposited-funds');
  }

  async addBuyerNote(content) {
    return this.request('/accounting/buyer-notes', {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
  }

  async deleteBuyerNote(id) {
    return this.request(`/accounting/buyer-notes/${id}`, {
      method: 'DELETE',
    });
  }

  // ============ DATA CLEANUP ============
  async cleanupOrphanedRecords() {
    return this.request('/accounting/cleanup-orphans', {
      method: 'POST',
    });
  }

  // ============ PENDING PAYMENTS ============
  async getPendingPayments() {
    return this.request('/accounting/pending-payments');
  }

  async getPendingPaymentsSummary() {
    return this.request('/accounting/pending-payments/summary');
  }

  async clearPendingPayment(id, data) {
    return this.request(`/accounting/pending-payments/${id}/clear`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async batchClearPendingPayments(data) {
    return this.request('/accounting/pending-payments/batch-clear', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async payoffFloorplan(vehicleId, data) {
    return this.request(`/accounting/floorplan/${vehicleId}/payoff`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getPendingTitles() {
    return this.request('/accounting/pending-titles');
  }

  async getPendingAuctionPayments() {
    return this.request('/accounting/pending-auction-payments');
  }

  async markTitleReceived(vehicleId, data) {
    return this.request(`/accounting/title-received/${vehicleId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getTitlesPendingVehicles() {
    return this.request('/vehicles/titles-pending');
  }

  async getTitleHistory(limit = 200) {
    return this.request(`/vehicles/title-history?limit=${limit}`);
  }

  async getVehicleTitleHistory(vehicleId) {
    return this.request(`/vehicles/${vehicleId}/title-history`);
  }

  async markAuctionPaymentReceived(vehicleId, data) {
    return this.request(`/accounting/auction-payment-received/${vehicleId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ============ SHOP EXPENSES ============
  async getShopExpenses(userId, search) {
    const params = new URLSearchParams();
    if (userId) params.append('userId', userId);
    if (search) params.append('search', search);
    const query = params.toString();
    return this.request(`/shop-expenses${query ? '?' + query : ''}`);
  }

  async recordPayrollPayment(data) {
    return this.request('/shop-expenses/payroll-payments', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getPayrollPayments(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/shop-expenses/payroll-payments${query ? '?' + query : ''}`);
  }

  async getShopExpensesForWeek(weekStart) {
    return this.request(`/shop-expenses/week?weekStart=${weekStart}`);
  }

  async getShopExpenseBalance(userId) {
    return this.request(`/shop-expenses/balance/${userId}`);
  }

  async createShopExpense(data) {
    return this.request('/shop-expenses', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteShopExpense(id) {
    return this.request(`/shop-expenses/${id}`, {
      method: 'DELETE',
    });
  }

  async updateShopExpense(id, data) {
    return this.request(`/shop-expenses/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // ============ PAYROLL ============
  async getTimeEntriesForWeek(weekStart) {
    return this.request(`/time-clock/week?weekStart=${weekStart}`);
  }

  async createContractorPayment(data) {
    return this.request('/accounting/contractor-payment', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ============ VENDOR SPEND ============
  async getVendorSpend() {
    return this.request('/vendors/spend');
  }

  // ============ TODOS ============
  async getTodos(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/todos${query ? `?${query}` : ''}`);
  }

  async getMyTodos() {
    return this.request('/todos/my');
  }

  async createTodo(data) {
    return this.request('/todos', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateTodo(id, data) {
    return this.request(`/todos/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async completeTodo(id) {
    return this.request(`/todos/${id}/complete`, { method: 'PUT' });
  }

  async deleteTodo(id) {
    return this.request(`/todos/${id}`, { method: 'DELETE' });
  }

  // ============ DEALS (Deal Desk) ============
  async getDeals(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/deals${query ? `?${query}` : ''}`);
  }

  async addVehicleCostAdjustment(data) {
    return this.request('/vehicle-cost-adjustments', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getVehicleCostAdjustments(vehicleId, params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/vehicle-cost-adjustments/vehicle/${vehicleId}${query ? `?${query}` : ''}`);
  }

  async voidVehicleCostAdjustment(id, reason) {
    return this.request(`/vehicle-cost-adjustments/${id}/void`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  async getDealsReport(startDate, endDate, { all } = {}) {
    const params = new URLSearchParams();
    if (all) {
      params.set('all', 'true');
    } else {
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
    }
    return this.request(`/deals/report?${params.toString()}`);
  }

  async updateVehicleBuyer(dealId, buyer) {
    return this.request(`/deals/${dealId}/vehicle-buyer`, {
      method: 'PATCH',
      body: JSON.stringify({ buyer }),
    });
  }

  async searchBuyers(query) {
    return this.request(`/deals/buyers/search?q=${encodeURIComponent(query)}`);
  }

  async createDeal(data) {
    return this.request('/deals', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getDealCustomers(q = '') {
    const query = q ? `?q=${encodeURIComponent(q)}` : '';
    return this.request(`/deal-customers${query}`);
  }
  async saveDealCustomer(data) {
    return this.request('/deal-customers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateDeal(id, data) {
    return this.request(`/deals/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async updateDealChecklist(id, field, value) {
    return this.request(`/deals/${id}/checklist`, {
      method: 'PUT',
      body: JSON.stringify({ field, value }),
    });
  }

  async recordDealPayment(id, data) {
    return this.request(`/deals/${id}/payment`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteDeal(id, reason) {
    return this.request(`/deals/${id}`, {
      method: 'DELETE',
      body: JSON.stringify({ reason }),
    });
  }

  async findDealByVin(vin) {
    return this.request(`/deals/find-by-vin/${vin}`);
  }

  async findVehicleByVin(vin) {
    return this.request(`/vehicles/find-by-vin/${vin}`);
  }

  // Deal Payments (partial payments / invoice system)
  async getDealPayments(dealId) {
    return this.request(`/deals/${dealId}/payments`);
  }

  async getInvoiceData(dealId) {
    return this.request(`/deals/${dealId}/invoice`);
  }

  async getReceiptData(dealId, paymentId) {
    return this.request(`/deals/${dealId}/payments/${paymentId}/receipt`);
  }

  async addDealPayment(dealId, data) {
    return this.request(`/deals/${dealId}/payments`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteDealPayment(dealId, paymentId) {
    return this.request(`/deals/${dealId}/payments/${paymentId}`, {
      method: 'DELETE',
    });
  }

  async markDealPaid(dealId, notes) {
    return this.request(`/deals/${dealId}/mark-paid`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    });
  }

  async updateDealChecklistExtended(id, field, value) {
    return this.request(`/deals/${id}/checklist-extended`, {
      method: 'PUT',
      body: JSON.stringify({ field, value }),
    });
  }

  async markDealPaidChecklist(id, data) {
    return this.request(`/deals/${id}/mark-paid-checklist`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async batchMarkDealsPaid(data) {
    return this.request('/deals/batch-mark-paid', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ============ BILLS ============
  async getBills(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/bills${query ? `?${query}` : ''}`);
  }

  async getBillsDue(days = 7) {
    return this.request(`/bills/due?days=${days}`);
  }

  async createBill(data) {
    return this.request('/bills', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateBill(id, data) {
    return this.request(`/bills/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async payBill(id, data) {
    return this.request(`/bills/${id}/pay`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteBill(id) {
    return this.request(`/bills/${id}`, { method: 'DELETE' });
  }

  // ============ RO APPROVAL ============
  async getPendingApprovalROs() {
    return this.request('/repair-orders/pending-approval');
  }

  async getFlaggedROs() {
    return this.request('/repair-orders/flagged');
  }

  async getOverdueROs() {
    return this.request('/repair-orders/overdue');
  }

  async flagRONotWorthIt(id, notes) {
    return this.request(`/repair-orders/${id}/flag-not-worth-it`, {
      method: 'PUT',
      body: JSON.stringify({ notes }),
    });
  }

  async makeROFlagDecision(id, decision, notes) {
    return this.request(`/repair-orders/${id}/flag-decision`, {
      method: 'PUT',
      body: JSON.stringify({ decision, notes }),
    });
  }

  // Legacy helper (kept to avoid name collision with approveRO above)
  async approveROLegacy(id) {
    return this.request(`/repair-orders/${id}/approve`, { method: 'POST' });
  }

  async rejectRO(id, reason) {
    return this.request(`/repair-orders/${id}/reject`, {
      method: 'PUT',
      body: JSON.stringify({ reason }),
    });
  }

  // ============ REPORTS ============
  async getPnLReport(startDate, endDate) {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return this.request(`/reports/pnl?${params.toString()}`);
  }

  // Performance P&L over any range: revenue - vehicle COGS = gross, minus operating = net
  // opts: { startDate, endDate, granularity: 'day'|'week'|'month', months }
  async getPerformanceReport(opts = {}) {
    const params = new URLSearchParams();
    if (opts.startDate) params.append('startDate', opts.startDate);
    if (opts.endDate) params.append('endDate', opts.endDate);
    if (opts.granularity) params.append('granularity', opts.granularity);
    if (opts.months) params.append('months', opts.months);
    return this.request(`/reports/performance?${params.toString()}`);
  }

  async getInventoryAgingReport() {
    return this.request('/reports/inventory-aging');
  }

  async getReconCostsReport(startDate, endDate) {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return this.request(`/reports/recon-costs?${params.toString()}`);
  }

  async getTransactionsReport(startDate, endDate, type) {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (type) params.append('type', type);
    return this.request(`/reports/transactions?${params.toString()}`);
  }

  async getPayrollReport(startDate, endDate) {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return this.request(`/reports/payroll?${params.toString()}`);
  }

  async getVendorSpendReport(startDate, endDate) {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return this.request(`/reports/vendor-spend?${params.toString()}`);
  }

  async getUtilitiesReport(year, scope = 'utilities') {
    const params = new URLSearchParams();
    if (year) params.append('year', year);
    if (scope) params.append('scope', scope);
    return this.request(`/reports/utilities?${params.toString()}`);
  }

  // ============ LEDGER ============
  async getLedger(startDate, endDate, type) {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (type) params.append('type', type);
    return this.request(`/accounting/ledger?${params.toString()}`);
  }

  async getSales(startDate, endDate) {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return this.request(`/accounting/sales?${params.toString()}`);
  }

  // ============ ADMIN TASKS ============
  async getWeeklyTasks(weekStart) {
    const params = weekStart ? `?weekStart=${weekStart}` : '';
    return this.request(`/admin-tasks/weekly${params}`);
  }

  async getTaskTemplates() {
    return this.request('/admin-tasks/templates');
  }

  async createTaskTemplate(data) {
    return this.request('/admin-tasks/templates', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateTaskTemplate(id, data) {
    return this.request(`/admin-tasks/templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteTaskTemplate(id) {
    return this.request(`/admin-tasks/templates/${id}`, {
      method: 'DELETE',
    });
  }

  async completeTask(templateId, data) {
    return this.request(`/admin-tasks/complete/${templateId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async uncompleteTask(templateId, weekStart) {
    const params = weekStart ? `?weekStart=${weekStart}` : '';
    return this.request(`/admin-tasks/complete/${templateId}${params}`, {
      method: 'DELETE',
    });
  }

  async getTaskHistory(templateId, weeks = 8) {
    const params = new URLSearchParams();
    if (templateId) params.append('templateId', templateId);
    params.append('weeks', weeks);
    return this.request(`/admin-tasks/history?${params.toString()}`);
  }

  async seedDefaultTasks() {
    return this.request('/admin-tasks/seed-defaults', {
      method: 'POST',
    });
  }

  // Sale List
  async getSaleList(weekStart) {
    const params = weekStart ? `?weekStart=${weekStart}` : '';
    return this.request(`/admin-tasks/sale-list${params}`);
  }

  async addToSaleList(data) {
    return this.request('/admin-tasks/sale-list/add', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSaleListVehicle(id, data) {
    return this.request(`/admin-tasks/sale-list/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async removeFromSaleList(id) {
    return this.request(`/admin-tasks/sale-list/${id}`, {
      method: 'DELETE',
    });
  }

  // Gas Receipts
  async getGasReceipts(startDate, endDate) {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return this.request(`/admin-tasks/gas-receipts?${params.toString()}`);
  }

  async createGasReceipt(data) {
    return this.request('/admin-tasks/gas-receipts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getGasReceiptPrint(id) {
    return this.request(`/admin-tasks/gas-receipts/${id}/print`);
  }

  // ============ SALES API ============

  // Tax rates and fees
  async getTaxRates() {
    return this.request('/sales/tax-rates');
  }

  async getFees() {
    return this.request('/sales/fees');
  }

  async getDealerInfo() {
    return this.request('/sales/dealer-info');
  }

  // Sale calculations
  async calculateSale(data) {
    return this.request('/sales/calculate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Retail sales
  async createRetailSale(data) {
    return this.request('/sales/retail', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async completeRetailSale(dealId, paymentConfirmed = true) {
    return this.request(`/sales/retail/${dealId}/complete`, {
      method: 'POST',
      body: JSON.stringify({ paymentConfirmed }),
    });
  }

  // Wholesale sales
  async createWholesaleSale(data) {
    return this.request('/sales/wholesale', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async completeWholesaleSale(dealId, paymentConfirmed = true) {
    return this.request(`/sales/wholesale/${dealId}/complete`, {
      method: 'POST',
      body: JSON.stringify({ paymentConfirmed }),
    });
  }

  // Deal management
  async getSalesDeals(filters = {}) {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.type) params.append('type', filters.type);
    return this.request(`/sales/deals?${params.toString()}`);
  }

  async getSalesDeal(id) {
    return this.request(`/sales/deals/${id}`);
  }

  async getAccountsReceivable() {
    return this.request('/deals/ar');
  }

  async getAllDealsDebug() {
    return this.request('/deals/debug/all');
  }

  async cancelDeal(id) {
    return this.request(`/sales/deals/${id}`, {
      method: 'DELETE',
    });
  }

  async updateDeal(id, data) {
    return this.request(`/sales/deals/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Get retail form pack PDF URL
  getRetailFormsPdfUrl(dealId) {
    return `${this.baseUrl}/sales/forms/retail/${dealId}`;
  }

  // Get wholesale bill of sale PDF URL
  getWholesaleFormPdfUrl(dealId) {
    return `${this.baseUrl}/sales/forms/wholesale/${dealId}`;
  }

  // Get single form PDF URL
  getSingleFormPdfUrl(formType, dealId) {
    return `${this.baseUrl}/sales/forms/single/${formType}/${dealId}`;
  }

  // ============ FORM TEMPLATES API ============

  // Get all form templates (optional packType: 'titles' for Titles module)
  async getFormTemplates(packType) {
    const qs = packType ? `?pack_type=${packType}` : '';
    return this.request(`/forms/templates${qs}`);
  }

  // Get single template
  async getFormTemplate(id) {
    return this.request(`/forms/templates/${id}`);
  }

  // Upload a multi-page PDF pack
  async uploadFormPack(file) {
    const token = this.accessToken || localStorage.getItem('lotsense_access_token');
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${this.baseUrl}/forms/templates/upload-pack`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || 'Upload failed');
    }
    return response.json();
  }

  // Split an uploaded PDF into individual templates
  async splitFormPack(filename, splits, fileUrl = null) {
    return this.request('/forms/templates/split', {
      method: 'POST',
      body: JSON.stringify({ filename, fileUrl, splits }),
    });
  }

  // Upload a new form template
  async uploadFormTemplate(formData) {
    const token = this.accessToken || localStorage.getItem('lotsense_access_token');
    const response = await fetch(`${this.baseUrl}/forms/templates`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData, // FormData object, not JSON
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || 'Upload failed');
    }
    return response.json();
  }

  // Update template metadata
  async updateFormTemplate(id, data) {
    return this.request(`/forms/templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Delete template
  async deleteFormTemplate(id) {
    return this.request(`/forms/templates/${id}`, {
      method: 'DELETE',
    });
  }

  // ============ FORM PACKS ============

  // Get all form packs (optional packType: 'titles' for Titles module)
  async getFormPacks(packType) {
    const qs = packType ? `?pack_type=${packType}` : '';
    return this.request(`/forms/packs${qs}`);
  }

  // Get templates in a specific pack (for titlesOnly when pack selected)
  async getPackTemplates(packId) {
    return this.request(`/forms/packs/${packId}/templates`);
  }

  async linkTemplatesToPack(packId, templateIds) {
    return this.request(`/forms/packs/${packId}/link-templates`, {
      method: 'POST',
      body: JSON.stringify({ templateIds }),
    });
  }

  async unlinkTemplateFromPack(packId, templateId) {
    return this.request(`/forms/packs/${packId}/link-templates/${templateId}`, {
      method: 'DELETE',
    });
  }

  // Create a blank letter-size PDF template (Titles module)
  async createBlankFormTemplate(name, formPackId) {
    return this.request('/forms/templates/blank', {
      method: 'POST',
      body: JSON.stringify({ name, form_pack_id: formPackId }),
    });
  }

  // Get all manual_entry fields for a pack's templates (to show input prompts before printing)
  async getPackManualFields(packId) {
    return this.request(`/forms/packs/${packId}/manual-fields`);
  }

  // Generate title forms for a vehicle (with optional manual values for fillable fields)
  async generateTitleForms(vehicleId, packId, manualValues = {}, templateIds = []) {
    return this.request('/forms/generate-title', {
      method: 'POST',
      body: JSON.stringify({ vehicleId, packId, manualValues, templateIds }),
    });
  }

  // ============ VINDEX (valuation intelligence + Get Buy Number) ============
  async getVindexSnapshot(vin) {
    return this.request(`/vindex/snapshot?vin=${encodeURIComponent(vin)}`);
  }

  // Checkpointed job — call repeatedly (~3s apart) until status === 'done'
  async vindexRerun(vin, miles, force = false) {
    return this.request('/vindex/rerun', {
      method: 'POST',
      body: JSON.stringify({ vin, miles, force }),
    });
  }

  // conditions: { accidents: "1", frame_damage: false, engine_issues: ["Runs Rough"], ... }
  async vindexExactOffer(vin, miles, conditions) {
    return this.request('/vindex/exact-offer', {
      method: 'POST',
      body: JSON.stringify({ vin, miles, conditions }),
    });
  }

  // ============ APP SETTINGS ============
  // NOTE: SettingsPage referenced these for ages but they never existed — the page
  // crashed on load (it was also unreachable from the nav until recently).
  async getSettings() {
    return this.request('/settings');
  }

  // Partial updates are safe: the server upserts only the keys provided
  async updateSettings(settings) {
    return this.request('/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  async getDealerSettings() {
    return this.request('/forms/dealer-settings');
  }

  async updateDealerSettings(data) {
    return this.request('/forms/dealer-settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Create a new form pack (optional packType: 'titles' | 'retail')
  async createFormPack(name, description, packType = 'retail') {
    return this.request('/forms/packs', {
      method: 'POST',
      body: JSON.stringify({ name, description, pack_type: packType }),
    });
  }

  // Update a form pack
  async updateFormPack(id, data) {
    return this.request(`/forms/packs/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Set a form pack as active
  async activateFormPack(id) {
    return this.request(`/forms/packs/${id}/activate`, {
      method: 'PUT',
    });
  }

  // Duplicate a form pack (grouping only; forms are linked, not copied)
  async duplicateFormPack(id) {
    return this.request(`/forms/packs/${id}/duplicate`, {
      method: 'POST',
    });
  }

  // Set the print order of a pack's forms (array of template ids in desired order)
  async reorderPackTemplates(packId, templateIds) {
    return this.request(`/forms/packs/${packId}/order`, {
      method: 'PUT',
      body: JSON.stringify({ templateIds }),
    });
  }

  // Delete a form pack (grouping only — the forms themselves are never deleted)
  async deleteFormPack(id) {
    return this.request(`/forms/packs/${id}`, {
      method: 'DELETE',
    });
  }

  // Get templates in a pack
  async getFormPackTemplates(packId) {
    return this.request(`/forms/packs/${packId}/templates`);
  }

  // Move template to different pack
  async moveTemplateTopack(templateId, packId) {
    return this.request(`/forms/templates/${templateId}/move`, {
      method: 'PUT',
      body: JSON.stringify({ packId }),
    });
  }

  // Save redactions for a template
  async saveTemplateRedactions(templateId, redactions) {
    return this.request(`/forms/templates/${templateId}/redactions`, {
      method: 'PUT',
      body: JSON.stringify({ redactions }),
    });
  }

  // Set rotation for a template
  async setTemplateRotation(templateId, rotation) {
    return this.request(`/forms/templates/${templateId}/rotation`, {
      method: 'PUT',
      body: JSON.stringify({ rotation }),
    });
  }

  // Delete ALL form templates (fresh start)
  async deleteAllFormTemplates() {
    return this.request('/forms/templates/all', {
      method: 'DELETE',
    });
  }

  // Get available forms for a deal (for selection modal).
  // packId scopes to that pack; all=true lists every active template in the system.
  async getAvailableForms(dealId, packId = null, all = false) {
    const qs = all ? '?all=true' : (packId ? `?packId=${packId}` : '');
    return this.request(`/forms/available/${dealId}${qs}`);
  }

  // Generate forms for a deal.
  //   templateIds: print exactly these forms in order
  //   packId: print that pack ('default' = active pack for the deal's sale type)
  //   manualValues: fill-in values keyed "field:<id>" / "<templateId>::<label>" / label
  async generateForms(dealId, templateIds, packId = null, manualValues = {}) {
    return this.request(`/forms/generate/${dealId}`, {
      method: 'POST',
      body: JSON.stringify({ templateIds, packId, manualValues }),
    });
  }

  // ============ FORM TEMPLATE FIELDS (per-field positioning) ============

  // Get all fields for a template
  async getFormTemplateFields(templateId) {
    return this.request(`/forms/templates/${templateId}/fields`);
  }

  // Add a field to a template
  async addFormTemplateField(templateId, field) {
    return this.request(`/forms/templates/${templateId}/fields`, {
      method: 'POST',
      body: JSON.stringify(field),
    });
  }

  // Update a field
  async updateFormTemplateField(templateId, fieldId, field) {
    return this.request(`/forms/templates/${templateId}/fields/${fieldId}`, {
      method: 'PUT',
      body: JSON.stringify(field),
    });
  }

  // Delete a field
  async deleteFormTemplateField(templateId, fieldId) {
    return this.request(`/forms/templates/${templateId}/fields/${fieldId}`, {
      method: 'DELETE',
    });
  }

  // Bulk update all fields for a template
  async bulkUpdateFormTemplateFields(templateId, fields) {
    return this.request(`/forms/templates/${templateId}/fields/bulk`, {
      method: 'POST',
      body: JSON.stringify({ fields }),
    });
  }

  // Generate preview with sample data
  async previewFormTemplate(templateId) {
    return this.request(`/forms/templates/${templateId}/preview`, {
      method: 'POST',
    });
  }

  // Get template PDF URL
  getFormTemplatePdfUrl(id) {
    const token = this.accessToken || localStorage.getItem('lotsense_access_token');
    return `${this.baseUrl}/forms/templates/${id}/pdf?token=${token}`;
  }

  // Generate a blank but fully fillable AcroForm PDF (all fields empty + typeable, no data pre-filled)
  async getBlankFillableTemplate(templateId) {
    return this.request(`/forms/templates/${templateId}/blank-fillable`, {
      method: 'POST',
    });
  }

  // Get blank template PDF URL
  getBlankTemplatePdfUrl(id) {
    const token = this.accessToken || localStorage.getItem('lotsense_access_token');
    return `${this.baseUrl}/forms/templates/${id}/blank?token=${token}`;
  }

  // Save redaction zones and create blank template
  async saveRedactionZones(templateId, redactionZones) {
    return this.request(`/forms/templates/${templateId}/redactions`, {
      method: 'POST',
      body: JSON.stringify({ redaction_zones: redactionZones }),
    });
  }

  // Use original PDF as blank (for pre-redacted uploads)
  async useOriginalAsBlank(templateId) {
    return this.request(`/forms/templates/${templateId}/use-original-as-blank`, {
      method: 'POST',
    });
  }

  // Reset template (clear blank file and mappings to start over)
  async resetFormTemplate(templateId) {
    return this.request(`/forms/templates/${templateId}/reset`, {
      method: 'POST',
    });
  }

  // Save field mappings
  async saveFieldMappings(templateId, fieldMappings) {
    return this.request(`/forms/templates/${templateId}/mappings`, {
      method: 'POST',
      body: JSON.stringify({ field_mappings: fieldMappings }),
    });
  }

  // Get available fields for mapping
  async getAvailableFields() {
    return this.request('/forms/fields');
  }

  // Generate filled forms for a deal
  async generateFilledForms(dealId, templateIds = null) {
    return this.request(`/forms/generate/${dealId}`, {
      method: 'POST',
      body: JSON.stringify({ templateIds }),
    });
  }

  // Get filled form PDF URL
  getFilledFormPdfUrl(filename) {
    return `${this.baseUrl}/forms/filled/${filename}`;
  }

  // Send forms for e-signature
  async sendForSignature(dealId, filename, signatureFields = []) {
    return this.request(`/forms/send-for-signature/${dealId}`, {
      method: 'POST',
      body: JSON.stringify({ filename, signatureFields }),
    });
  }

  // Get signature status for a deal
  async getSignatureStatus(dealId) {
    return this.request(`/forms/signature-status/${dealId}`);
  }

  // Check/refresh signature status
  async checkSignatureStatus(requestId) {
    return this.request(`/forms/check-signature/${requestId}`, {
      method: 'POST',
    });
  }

  // Get signed document PDF URL
  getSignedFormPdfUrl(filename) {
    return `${this.baseUrl}/forms/signed/${filename}`;
  }

  // ============ BILL PAYMENT CHECKLIST ============

  async getBillLocations() {
    return this.request('/accounting/bill-locations');
  }

  async createBillLocation(data) {
    return this.request('/accounting/bill-locations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createBillType(data) {
    return this.request('/accounting/bill-types', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteBillType(id) {
    return this.request(`/accounting/bill-types/${id}`, {
      method: 'DELETE',
    });
  }

  async getBillPayments(year, month) {
    return this.request(`/accounting/bill-payments/${year}/${month}`);
  }

  async toggleBillPayment(data) {
    return this.request('/accounting/bill-payments/toggle', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getReconciliation(bankAccountId, startDate, endDate) {
    let url = `/accounting/reconciliation/${bankAccountId}`;
    const params = [];
    if (startDate) params.push(`startDate=${startDate}`);
    if (endDate) params.push(`endDate=${endDate}`);
    if (params.length) url += '?' + params.join('&');
    return this.request(url);
  }

  async deleteTransaction(type, id) {
    return this.request(`/accounting/transactions/${type}/${id}`, { method: 'DELETE' });
  }

  // Backup & Reports
  async runBackup() {
    return this.request('/backup/run', { method: 'POST' });
  }

  async getBackupStatus() {
    return this.request('/backup/status');
  }

  async getBackupConfig() {
    return this.request('/backup/config');
  }

  getBackupDownloadUrl(filename) {
    return `${this.baseUrl}/backup/download/${filename}`;
  }

  // Month Closure
  async getMonthClosure(year, month) {
    return this.request(`/accounting/month-closure/${year}/${month}`);
  }

  async getMonthSummary(year, month) {
    return this.request(`/accounting/month-summary/${year}/${month}`);
  }

  async backfillSaleDates() {
    return this.request('/admin-tasks/backfill-sale-dates', {
      method: 'POST'
    });
  }

  async closeMonth(year, month, summary) {
    return this.request('/accounting/close-month', {
      method: 'POST',
      body: JSON.stringify({ year, month, summary })
    });
  }

  // Activities
  async getActivities(filter = 'today', userId = null) {
    const params = new URLSearchParams({ filter });
    if (userId && userId !== 'all') params.append('userId', userId);
    return this.request(`/activities?${params}`);
  }

  async getActivitySummary(filter = 'today') {
    return this.request(`/activities/summary?filter=${filter}`);
  }

  async logActivity(action, module, entityType = null, entityId = null, details = null, description = null) {
    return this.request('/activities', {
      method: 'POST',
      body: JSON.stringify({ action, module, entityType, entityId, details, description })
    });
  }

  // ============ VEHICLE FILES (Master Photo Library) ============
  
  async getVehicleThumbnails() {
    return this.request('/vehicle-files/thumbnails');
  }

  async getVehicleFiles(vehicleId) {
    return this.request(`/vehicle-files/${vehicleId}`);
  }

  async getROFiles(roId) {
    return this.request(`/vehicle-files/ro/${roId}`);
  }

  async getVehicleFileCount(vehicleId) {
    return this.request(`/vehicle-files/count/${vehicleId}`);
  }

  async getVehiclesWithFiles() {
    return this.request('/vehicle-files/vehicles-with-files');
  }

  async addVehicleFile(data) {
    return this.request('/vehicle-files', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async addVehicleFilesBulk(vehicleId, files, repairOrderId = null) {
    return this.request('/vehicle-files/bulk', {
      method: 'POST',
      body: JSON.stringify({ vehicleId, files, repairOrderId })
    });
  }

  async updateVehicleFile(id, data) {
    return this.request(`/vehicle-files/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteVehicleFile(id) {
    return this.request(`/vehicle-files/${id}`, {
      method: 'DELETE'
    });
  }

  async syncVehicleFiles(vehicleId) {
    return this.request(`/vehicle-files/sync/${vehicleId}`, {
      method: 'POST'
    });
  }

  // ============ AI INSPECTION ============
  
  async startAIInspection(data) {
    return this.request('/ai-inspection/start', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async completeAIInspection(data) {
    return this.request('/ai-inspection/complete', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async saveInspectionReport(data) {
    return this.request('/ai-inspection/save-report', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async getInspectionSessions(filters = {}) {
    const params = new URLSearchParams(filters).toString();
    return this.request(`/ai-inspection/sessions${params ? '?' + params : ''}`);
  }

  // ============ ALERT SUBSCRIPTIONS ============
  
  async getAlertSubscriptions() {
    return this.request('/alerts');
  }

  async getAlertTypes() {
    return this.request('/alerts/types');
  }

  async getCarriers() {
    return this.request('/alerts/carriers');
  }

  async createAlertSubscription(data) {
    return this.request('/alerts', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateAlertSubscription(id, data) {
    return this.request(`/alerts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteAlertSubscription(id) {
    return this.request(`/alerts/${id}`, {
      method: 'DELETE'
    });
  }

  async sendTestAlert(data) {
    return this.request('/alerts/send', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  // ============ BHPH PORTFOLIO (siloed) ============

  async getBhphCompany() {
    return this.request('/bhph/company');
  }

  async saveBhphCompany(data) {
    return this.request('/bhph/company', { method: 'PUT', body: JSON.stringify(data) });
  }

  async getBhphSummary() {
    return this.request('/bhph/summary');
  }

  async getBhphAccounts(params = {}) {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v != null && v !== '') query.append(k, v);
    });
    const qs = query.toString();
    return this.request(`/bhph/accounts${qs ? '?' + qs : ''}`);
  }

  async getBhphAccount(id) {
    return this.request(`/bhph/accounts/${id}`);
  }

  async createBhphAccount(data) {
    return this.request('/bhph/accounts', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateBhphAccount(id, data) {
    return this.request(`/bhph/accounts/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async createBhphPayment(accountId, data) {
    return this.request(`/bhph/accounts/${accountId}/payments`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async voidBhphPayment(paymentId) {
    return this.request(`/bhph/payments/${paymentId}/void`, { method: 'POST' });
  }

  async getBhphDocuments(accountId) {
    return this.request(`/bhph/accounts/${accountId}/documents`);
  }

  async saveBhphDocument(accountId, data) {
    return this.request(`/bhph/accounts/${accountId}/documents`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getBhphProfitabilityByCustomer() {
    return this.request('/bhph/profitability-by-customer');
  }

  async createBhphLateEvent(accountId, data) {
    return this.request(`/bhph/accounts/${accountId}/late-events`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteBhphLateEvent(eventId) {
    return this.request(`/bhph/late-events/${eventId}`, { method: 'DELETE' });
  }

  async loadBhphSamplePortfolio(replace = false, count = 40) {
    return this.request('/bhph/sample-portfolio', {
      method: 'POST',
      body: JSON.stringify({ replace, count }),
    });
  }
}

const api = new ApiService();
export default api;
